import { Component, type ReactNode } from 'react';

/**
 * Catches dynamic-import / chunk-load failures from `lazy()` screens — the classic
 * "stale bundle after deploy" white-screen. When a user keeps a tab open across a
 * prod deploy, the old index.html references chunk hashes the server has already
 * replaced; the next lazy `import()` 404s → unhandled rejection → React unmounts the
 * tree → blank page (there was no boundary). This boundary detects chunk-shaped
 * errors and does a ONE-TIME hard reload to pull the fresh bundle; a sessionStorage
 * guard prevents reload loops when the chunk is genuinely gone. Non-chunk render
 * errors show a manual-retry card instead of reloading (so a real render bug isn't
 * masked by an infinite refresh).
 *
 * Pairs with the global `vite:preloadError` listener in App (primary defense); this
 * boundary is the render-time backstop. Added 8 JUN 2026 after a learner hit a blank
 * page on «перейти к тесту» (FinalTest lazy chunk 404 right after a prod deploy).
 */

const RELOAD_GUARD_KEY = 'qastudio.v1.chunkReloadAt';
const RELOAD_GUARD_MS = 10_000;

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /ChunkLoadError|Loading chunk \d|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    msg,
  );
}

/**
 * Hard-reload once to recover from a stale-bundle chunk miss. Guarded by a
 * sessionStorage timestamp so a chunk that is genuinely unavailable can't trap the
 * tab in a refresh loop. Returns true if it triggered a reload.
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_MS) {
      return false; // already reloaded very recently — avoid a loop
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — proceed with one reload.
  }
  window.location.reload();
  return true;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  errored: boolean;
}

export class ChunkReloadBoundary extends Component<Props, State> {
  override state: State = { errored: false };

  static getDerivedStateFromError(): State {
    return { errored: true };
  }

  override componentDidCatch(error: unknown): void {
    // Side effects belong here, not in getDerivedStateFromError. A chunk-shaped
    // error means the bundle moved under us → reload to fetch the fresh build.
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk();
    }
  }

  override render(): ReactNode {
    if (this.state.errored) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div
          className="screen"
          style={{ display: 'grid', placeItems: 'center', minHeight: '60vh', padding: 24, textAlign: 'center' }}
        >
          <div className="state-card" style={{ maxWidth: 360 }}>
            <h2 style={{ fontFamily: 'var(--ff-display)', marginTop: 0 }}>Не удалось загрузить экран</h2>
            <p style={{ color: 'var(--ink-soft)', lineHeight: 1.5, margin: '0 0 16px' }}>
              Похоже, вышло обновление. Обнови страницу — подтянется свежая версия.
            </p>
            <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
              Обновить
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
